import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import {
  CalendarMonthOutlined as ScheduleIcon,
  ConfirmationNumberOutlined as TicketIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  FolderOutlined as ProjectIcon,
  NotesOutlined as DescriptionIcon,
  TuneOutlined as WindowIcon,
} from '@mui/icons-material';
import ProjectSectionFields, { PROJECT_SECTIONS } from './ProjectFields';
import RegexHelp from './RegexHelp';
import StatusChip from './StatusChip';
import { cardSx, GREEN, INK, MUTED } from '../home/homeTheme';
import { deleteProject, fetchDeleteBlockers, fmtDay, isOpenEnded, projectToForm, updateProject, validateProjectForm } from '../../utils/projectsService';
import { parseLocalDate } from '../../utils/dateHelpers';
import { ENABLE_TAB_CONTRACT } from '../../config/local.config';

const ICONS = { project: ProjectIcon, schedule: ScheduleIcon, ticket: TicketIcon, window: WindowIcon, description: DescriptionIcon };
const monoSx = { fontFamily: 'monospace', fontSize: 13 };
const fmtStamp = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function duration(start, end) {
  if (!start || isOpenEnded(end)) return 'Open-ended';
  const a = parseLocalDate(String(start).slice(0, 10));
  // Fine inclusa: 1 Jan – 31 Dec è un anno intero
  const b = parseLocalDate(String(end).slice(0, 10));
  b.setDate(b.getDate() + 1);
  const months = (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth() + (b.getDate() >= a.getDate() ? 0 : -1);
  if (months < 1) return `${Math.round((b - a) / 86400000) + 1} days`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y && `${y} year${y === 1 ? '' : 's'}`, m && `${m} month${m === 1 ? '' : 's'}`].filter(Boolean).join(' ');
}

/** Riga etichetta / valore delle card in sola lettura. */
function Field({ label, children, empty }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
      <Box sx={{ fontSize: 14, color: empty ? MUTED : INK, mt: 0.25, wordBreak: 'break-word' }}>{empty ? 'Not set' : children}</Box>
    </Box>
  );
}

/** Card di una sezione: in lettura con "Edit", in modifica con i campi e Cancel/Save. */
function SectionCard({ section, editing, disabledEdit, onEdit, onCancel, onSave, saving, error, children, form, full }) {
  const Icon = ICONS[section];
  return (
    <Box sx={{ ...cardSx, p: 2.25, gridColumn: full ? '1 / -1' : undefined, ...(editing ? { borderColor: GREEN, boxShadow: '0 0 0 3px rgba(90,176,49,0.12)' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
        <Icon sx={{ fontSize: 20, color: MUTED }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, flex: 1 }}>{PROJECT_SECTIONS[section].title}</Typography>
        {!editing && onEdit && (
          <Button size="small" color="inherit" startIcon={<EditIcon />} onClick={onEdit} disabled={disabledEdit} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
            Edit
          </Button>
        )}
      </Box>
      {editing ? (
        <>
          {form}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button color="inherit" onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={onSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 110, bgcolor: GREEN }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save changes'}
            </Button>
          </Box>
        </>
      ) : (
        children
      )}
    </Box>
  );
}

const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 1.75, columnGap: 2 };

/**
 * Scheda Overview del progetto: le sezioni del progetto in card, ognuna modificabile da sola
 * (una alla volta), e l'eliminazione del progetto in fondo.
 */
export default function ProjectOverview({ project, customers, serviceTypes, userId, canEdit, canDelete, onSaved, onDeleted, onDirtyChange, onOpenCustomer }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => projectToForm(project));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [regexHelp, setRegexHelp] = useState(false);

  const original = projectToForm(project);
  const dirty = !!editing && JSON.stringify(form) !== JSON.stringify(original);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const startEdit = (section) => {
    setForm(projectToForm(project));
    setErrors({});
    setEditing(section);
  };
  const cancel = () => {
    setEditing(null);
    setErrors({});
  };
  const change = (field, value) => {
    setForm((f) => ({ ...f, [field]: value, ...(field === 'customer_id' ? { yubiq_code: '' } : {}) }));
    setErrors((e) => ({ ...e, [field]: undefined, general: undefined }));
  };
  const save = async () => {
    const e = validateProjectForm(form, PROJECT_SECTIONS[editing].fields);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await updateProject(project.id, form, userId);
      await onSaved('Project updated');
      setEditing(null);
    } catch (err) {
      setErrors({ general: `Not saved: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const card = (section, readView, full) => (
    <SectionCard
      section={section}
      full={full}
      editing={editing === section}
      disabledEdit={!!editing}
      onEdit={canEdit ? () => startEdit(section) : null}
      onCancel={cancel}
      onSave={save}
      saving={saving}
      error={errors.general}
      form={
        <ProjectSectionFields
          section={section}
          form={form}
          errors={errors}
          onChange={change}
          customers={customers}
          serviceTypes={serviceTypes}
          onRegexHelp={() => setRegexHelp(true)}
          autoFocus
        />
      }
    >
      {readView}
    </SectionCard>
  );

  const customer = project.customer || customers.find((c) => String(c.id) === String(project.customer_id));
  const serviceLabel = serviceTypes.find((o) => o.value === project.tipologia_servizio)?.label || project.tipologia_servizio;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' }}>
        {card(
          'project',
          <Box sx={grid2}>
            <Field label="Project code" empty={!project.project_code}>
              <Box component="span" sx={monoSx}>
                {project.project_code}
              </Box>
            </Field>
            <Field label="Customer" empty={!customer}>
              {customer && (
                <Link component="button" type="button" underline="hover" onClick={() => onOpenCustomer(customer.id)} sx={{ fontSize: 14, color: INK, fontWeight: 600, textAlign: 'left' }}>
                  {customer.name}
                </Link>
              )}
            </Field>
            {ENABLE_TAB_CONTRACT && (
              <>
                <Field label="Service type" empty={!project.tipologia_servizio}>
                  {serviceLabel}
                </Field>
                <Field label="Yubiq code" empty={!project.yubiq_code}>
                  <Box component="span" sx={monoSx}>
                    {project.yubiq_code}
                  </Box>
                </Field>
              </>
            )}
            <Field label="Status">
              <StatusChip active={project.active !== false} />
            </Field>
          </Box>,
        )}
        {card(
          'schedule',
          <Box sx={grid2}>
            <Field label="Start date" empty={!project.start_date}>
              {fmtDay(project.start_date)}
            </Field>
            <Field label="End date">{isOpenEnded(project.end_date) ? 'No end date' : fmtDay(project.end_date)}</Field>
            <Field label="Duration">{duration(project.start_date, project.end_date)}</Field>
          </Box>,
        )}
        {card(
          'ticket',
          <Box sx={grid2}>
            <Field label="Ticket ID prefix" empty={!project.ticket_id_prefix}>
              <Box component="span" sx={monoSx}>
                {project.ticket_id_prefix}
              </Box>
            </Field>
            <Field label="Regex pattern" empty={!project.regex}>
              <Box component="span" sx={monoSx}>
                {project.regex}
              </Box>
            </Field>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Field label="Regex description" empty={!project.regex_desc}>
                {project.regex_desc}
              </Field>
            </Box>
          </Box>,
        )}
        {card(
          'window',
          <Box sx={grid2}>
            <Field label="Days before today">
              {project.days_before_block || 1} day{(project.days_before_block || 1) === 1 ? '' : 's'}
            </Field>
            <Field label="Days after today">
              {project.days_after_block || 1} day{(project.days_after_block || 1) === 1 ? '' : 's'}
            </Field>
            <Field label="Previous month">{project.previous_month_blocked ? 'Blocked' : 'Open'}</Field>
          </Box>,
        )}
        {card(
          'description',
          <Typography sx={{ fontSize: 14, color: project.description ? INK : MUTED, whiteSpace: 'pre-line' }}>{project.description || 'No description.'}</Typography>,
          true,
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', pt: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          Project ID {project.id} · created {fmtStamp(project.created_at)} · last updated {fmtStamp(project.updated_at)}
        </Typography>
        {canDelete && <DeleteProject project={project} onDeleted={onDeleted} />}
      </Box>

      <RegexHelp
        open={regexHelp}
        onClose={() => setRegexHelp(false)}
        regex={form.regex}
        onUse={(regex) => {
          change('regex', regex);
          setRegexHelp(false);
        }}
      />
    </Box>
  );
}

/** Eliminazione: bloccata (con il motivo) se il progetto ha worklog o report mapping; altrimenti con conferma. */
function DeleteProject({ project, onDeleted }) {
  const [blockers, setBlockers] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchDeleteBlockers(project.id)
      .then((b) => !cancelled && setBlockers(b))
      .catch(() => !cancelled && setBlockers({ unknown: true }));
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  const count = (n, one, many) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;
  const reasons = [
    blockers?.worklogs && count(blockers.worklogs, 'worklog', 'worklogs'),
    blockers?.users && `${count(blockers.users, 'user', 'users')} (People tab)`,
    blockers?.teams && `${count(blockers.teams, 'team', 'teams')} (People tab)`,
    blockers?.activities && `${count(blockers.activities, 'activity', 'activities')} (Activities tab)`,
    blockers?.mappings && 'report mappings (Report mapping tab)',
  ].filter(Boolean);
  const blocked = !blockers || blockers.unknown || reasons.length > 0;

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteProject(project.id);
      setConfirm(false);
      onDeleted('Project deleted');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {blockers && reasons.length > 0 && <Typography sx={{ fontSize: 12.5, color: MUTED }}>To delete it, first remove: {reasons.join(', ')}.</Typography>}
      {blockers?.unknown && <Typography sx={{ fontSize: 12.5, color: MUTED }}>Can’t check whether it can be deleted right now.</Typography>}
      <Button color="error" size="small" startIcon={<DeleteIcon />} disabled={blocked} onClick={() => setConfirm(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Delete project
      </Button>
      <Dialog open={confirm} onClose={() => !deleting && setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete {project.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">The project will be removed permanently.</Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirm(false)} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={remove} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
